/**
 * Polynomial arithmetic presets for the polynomial-arithmetic experiment.
 * Covers multiplication, long division, synthetic division, and GCD.
 */

export interface PolyArithPreset {
  id: string;
  mode: 'multiply' | 'long-divide' | 'synthetic' | 'gcd';
  a: string;
  b?: string;
  root?: string;
}

export const POLYARITH_PRESETS: readonly PolyArithPreset[] = [
  // Foundation: Multiplication
  {
    id: 'multiply-1',
    mode: 'multiply',
    a: 'x + 2',
    b: '2*x^2 + 3*x - 1',
  },
  {
    id: 'multiply-2',
    mode: 'multiply',
    a: 'x + 1',
    b: 'x + 1',
  },
  {
    id: 'multiply-3',
    mode: 'multiply',
    a: 'x - 1',
    b: 'x + 1',
  },

  // Foundation: Long Division
  {
    id: 'long-divide-1',
    mode: 'long-divide',
    a: 'x^3 + 2*x^2 - 5*x + 1',
    b: 'x - 1',
  },
  {
    id: 'long-divide-2',
    mode: 'long-divide',
    a: 'x^2 + 3*x - 4',
    b: 'x + 4',
  },
  {
    id: 'long-divide-3',
    mode: 'long-divide',
    a: '2*x^3 + 3*x^2 - 5*x - 6',
    b: 'x + 2',
  },

  // Advanced: Synthetic Division
  {
    id: 'synthetic-1',
    mode: 'synthetic',
    a: 'x^3 + 2*x^2 - 5*x + 1',
    root: '1',
  },
  {
    id: 'synthetic-2',
    mode: 'synthetic',
    a: 'x^3 - 6*x^2 + 11*x - 6',
    root: '2',
  },
  {
    id: 'synthetic-3',
    mode: 'synthetic',
    a: '2*x^3 - 3*x^2 + 5*x - 6',
    root: '3/2',
  },

  // Advanced: GCD
  {
    id: 'gcd-1',
    mode: 'gcd',
    a: 'x^2 - 1',
    b: 'x^2 + 2*x + 1',
  },
  {
    id: 'gcd-2',
    mode: 'gcd',
    a: 'x^3 - 1',
    b: 'x^2 + x + 1',
  },
  {
    id: 'gcd-3',
    mode: 'gcd',
    a: 'x^2 + 3*x + 2',
    b: 'x^2 + 5*x + 6',
  },
];

/**
 * Validate that a preset has all required fields and valid structure.
 */
export function isValidPreset(p: PolyArithPreset): boolean {
  // id must be non-empty string
  if (typeof p.id !== 'string' || p.id.length === 0) {
    return false;
  }

  // mode must be one of the valid modes
  if (!['multiply', 'long-divide', 'synthetic', 'gcd'].includes(p.mode)) {
    return false;
  }

  // a must be non-empty string (polynomial)
  if (typeof p.a !== 'string' || p.a.length === 0) {
    return false;
  }

  // b is required for multiply, long-divide, and gcd
  if (['multiply', 'long-divide', 'gcd'].includes(p.mode)) {
    if (!p.b || typeof p.b !== 'string' || p.b.length === 0) {
      return false;
    }
  }

  // root is required for synthetic
  if (p.mode === 'synthetic') {
    if (!p.root || typeof p.root !== 'string' || p.root.length === 0) {
      return false;
    }
  }

  return true;
}

/**
 * Check that all preset IDs are unique.
 */
export function allPresetsHaveUniqueIds(): boolean {
  const ids = new Set<string>();
  for (const preset of POLYARITH_PRESETS) {
    if (ids.has(preset.id)) {
      return false;
    }
    ids.add(preset.id);
  }
  return true;
}
